import { Alert, Button, Card, Form, Input, InputNumber, Space } from "antd";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    DatePicker as ZhDatePicker,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem
} from "zhihao-ui-react";

import request from "@/api";
import type { GroupPurchaseFormValues, GroupPurchaseRow } from "./types";

type GroupPurchaseFormPayload = GroupPurchaseFormValues & {
    imgMainId?: number;
    videoId?: number;
};

interface GroupPurchaseUploadedFile {
    id: number;
    path?: string;
}

export interface GroupPurchaseFormPageProps {
    loadGroupPurchase?: (id: number | string) => Promise<GroupPurchaseRow>;
    saveGroupPurchase?: (id: number | string | undefined, values: GroupPurchaseFormPayload) => Promise<void>;
}

/** 复用 Vue 团购商品详情接口，为编辑表单回填数据。 */
export async function loadGroupPurchase(id: number | string): Promise<GroupPurchaseRow> {
    const response = await request.service.get<GroupPurchaseRow>(`/admin/product-group-purchase/vo/${id}`);
    return response.data;
}

/** 复用 Vue 团购商品新增草稿和更新草稿接口。 */
export async function saveGroupPurchase(
    id: number | string | undefined,
    values: GroupPurchaseFormPayload
): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/product-group-purchase/create-save", values);
    } else {
        await request.service.post(`/admin/product-group-purchase/update-save/${id}`, values);
    }
}

/** 复用 Vue 上传组件的 FormData.file 契约，返回文件 ID 供表单字段回填。 */
export async function uploadGroupPurchaseMedia(file: Blob): Promise<GroupPurchaseUploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const { fileService } = await import("@/api/services/fileService");
    const uploadedFile = await fileService.uploadFile(formData);
    if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
    return { id: Number(uploadedFile.id), path: uploadedFile.path };
}

/** 团购商品新增/编辑页，保留 Vue 表单的核心价格、数量和介绍字段。 */
export function GroupPurchaseFormPage({
    loadGroupPurchase: load = loadGroupPurchase,
    saveGroupPurchase: save = saveGroupPurchase
}: GroupPurchaseFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const id = query.get("id") || undefined;
    const pageTitle = id ? "编辑团购商品" : "新增团购商品";
    const [form] = Form.useForm<GroupPurchaseFormValues>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [imgMainId, setImgMainId] = useState<number>();
    const [videoId, setVideoId] = useState<number>();
    const [imageUploading, setImageUploading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({ title: "", weight: 0 });
            setImgMainId(undefined);
            setVideoId(undefined);
            return;
        }

        void load(id)
            .then(result => {
                setErrorMessage(null);
                form.setFieldsValue(result.productGroupPurchase as GroupPurchaseFormValues);
                setImgMainId(result.mainFile?.id);
                setVideoId(result.videoFile?.id);
            })
            .catch(() => setErrorMessage("团购商品详情加载失败"));
    }, [form, id, load]);

    /** 对齐 Vue 主图上传限制：仅 JPEG/PNG，单文件不超过 2MB。 */
    const onMainImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!(file.type === "image/jpeg" || file.type === "image/png")) {
            setErrorMessage("主图仅支持 JPEG、PNG 格式");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setErrorMessage("主图大小不能超过 2MB");
            return;
        }
        setImageUploading(true);
        void uploadGroupPurchaseMedia(file)
            .then(uploadedFile => {
                setImgMainId(uploadedFile.id);
                setErrorMessage(null);
            })
            .catch(() => setErrorMessage("主图上传失败"))
            .finally(() => setImageUploading(false));
    };

    /** 对齐 Vue 视频上传限制：支持 MP4/MOV/MKV，单文件不超过 200MB。 */
    const onVideoChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        const videoTypes = ["video/mp4", "video/quicktime", "video/x-matroska"];
        if (!videoTypes.includes(file.type)) {
            setErrorMessage("视频仅支持 MP4、MOV、MKV 格式");
            return;
        }
        if (file.size > 200 * 1024 * 1024) {
            setErrorMessage("视频大小不能超过 200MB");
            return;
        }
        setVideoUploading(true);
        void uploadGroupPurchaseMedia(file)
            .then(uploadedFile => {
                setVideoId(uploadedFile.id);
                setErrorMessage(null);
            })
            .catch(() => setErrorMessage("视频上传失败"))
            .finally(() => setVideoUploading(false));
    };

    const submit = async () => {
        try {
            const values = await form.validateFields();
            await save(id, {
                ...values,
                ...(imgMainId == null ? {} : { imgMainId }),
                ...(videoId == null ? {} : { videoId })
            });
            void navigate("/productManagement/groupPurchase");
        } catch {
            setErrorMessage("团购商品保存失败");
        }
    };

    return (
        <section aria-label="group-purchase-form-page">
            <Card>
                <ZhDetailHeader title={pageTitle} />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Form form={form} layout="vertical">
                    {/* 用 zhihao 网格承载现有字段，保留 Ant Form 的校验和字段契约。 */}
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem span={2}>
                            <Form.Item
                                label="商品标题"
                                name="title"
                                rules={[{ required: true, message: "请输入商品标题" }]}
                            >
                                <Input />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="权重值" name="weight">
                                <InputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="拼团单价" name="groupUnitPrice">
                                <InputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="拼团最低吨数" name="minOrderTon">
                                <InputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="拼团截止时间" name="groupEndTime">
                                <ZhDatePicker
                                    className="w-full"
                                    format="YYYY-MM-DD HH:mm"
                                    placeholder="请选择拼团截止时间"
                                    type="datetime"
                                    valueFormat="YYYY-MM-DD HH:mm:ss"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="规格" name="productTypeSpec">
                                <Input />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="商品介绍" name="introduce">
                                <Input.TextArea rows={5} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="主图">
                                <input
                                    aria-label="主图上传"
                                    accept="image/jpeg,image/png"
                                    disabled={imageUploading}
                                    onChange={onMainImageChange}
                                    type="file"
                                />
                                {imageUploading ? <span>主图上传中...</span> : null}
                                {imgMainId ? <span>主图已上传：{imgMainId}</span> : null}
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="视频">
                                <input
                                    aria-label="视频上传"
                                    accept="video/mp4,video/quicktime,video/x-matroska"
                                    disabled={videoUploading}
                                    onChange={onVideoChange}
                                    type="file"
                                />
                                {videoUploading ? <span>视频上传中...</span> : null}
                                {videoId ? <span>视频已上传：{videoId}</span> : null}
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                    <Space>
                        <Button type="primary" onClick={() => void submit()}>
                            保存团购商品
                        </Button>
                        <Button onClick={() => void navigate("/productManagement/groupPurchase")}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}

export default GroupPurchaseFormPage;
